/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { Form } from "grab-ui"
import { Select, Tooltip } from "@duxton-web/ui"
import React, { useEffect, useRef, useState } from "react"
import { useTranslations } from "common/utils/i18n"
import { isUndefined } from "lodash"
import { storageHelper } from "utils/storage"
import { TOBACCO } from "../ItemDrawer/constant"

export interface TobaccoTaggingProps extends FormComponentProps {
  tobaccoPrediction?: boolean
  specialItemType?: string
  showAttributeDrawer?: boolean
  onTobaccoChange: (value: number) => void
}

const TobaccoTagging = ({
  form,
  tobaccoPrediction,
  specialItemType,
  showAttributeDrawer,
  onTobaccoChange,
}: TobaccoTaggingProps) => {
  const [showTooltip, setShowTooltip] = useState(false)

  const [yesText, noText, tooltipText, formItemLabel] = useTranslations([
    "common.yes",
    "common.no",
    "tobaccoSetting.infoTooltip",
    "tobaccoSetting.label",
  ])
  const options = [
    { label: yesText, value: 1 },
    { label: noText, value: 0 },
  ]
  const ref = useRef<HTMLDivElement>(null)

  const { getFieldDecorator } = form

  const tobaccoTaggingTooltipShowed = storageHelper.tobaccoTaggingTooltipShowed()

  useEffect(() => {
    if (
      showAttributeDrawer &&
      tobaccoPrediction &&
      !tobaccoTaggingTooltipShowed
    ) {
      ref?.current?.scrollIntoView({ behavior: "smooth", block: "center" })
      setShowTooltip(true)
      storageHelper.setTobaccoTaggingTooltipShowed()
    }
  }, [showAttributeDrawer, tobaccoPrediction, tobaccoTaggingTooltipShowed])

  const getTobaccoInitialValue = (): number | undefined => {
    if (!isUndefined(tobaccoPrediction)) {
      return Number(tobaccoPrediction)
    }
    if (specialItemType === TOBACCO) {
      return 1
    }
    return undefined
  }

  const handleChange = (value: number) => {
    onTobaccoChange(value)
  }

  const handleTooltipOpenChange = (open: boolean) => {
    if (!open) {
      setShowTooltip(false)
    }
  }

  return (
    <div
      ref={ref}
      style={{ position: "relative", marginBottom: "26px" }}
      data-testid="tobacco-setting"
    >
      <Form.Item label={formItemLabel}>
        {getFieldDecorator("tobaccoTagging", {
          initialValue: getTobaccoInitialValue(),
        })(
          <Select style={{ width: "100%" }} onChange={handleChange}>
            {options.map(({ value, label }) => (
              <Select.Option key={value} value={value}>
                {label}
              </Select.Option>
            ))}
          </Select>,
        )}
      </Form.Item>
      <Tooltip
        placement="bottomLeft"
        title={tooltipText}
        defaultOpen
        open={showTooltip}
        onOpenChange={handleTooltipOpenChange}
        destroyTooltipOnHide
        trigger="click"
      >
        <div style={{ marginTop: -8, position: "relative" }} />
      </Tooltip>
    </div>
  )
}
export default TobaccoTagging
